import { computed, nextTick, shallowRef, watch } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useHostElement } from "#ce/composables/useHostElement";
import { firstDetail } from "#ce/utils/events";

/**
 * Coordinates the activator and picker: focus, keyboard hints, and mobile commit timing.
 *
 * @param {object} options
 * @param {ReturnType<import('#ce/shared/filters/composables/useQuantumSearchField').useQuantumSearchField>} options.field
 * @param {ReturnType<import('./useEventsOmnisearchQuery').useEventsOmnisearchQuery>} options.query
 * @param {import('vue').Ref<HTMLElement | null>} options.input
 * @param {import('vue').Ref<HTMLElement | null>} options.select
 * @param {import('vue').ComputedRef<boolean>} options.editable
 */
export function useEventsOmnisearchInteraction({ field, query, input, select, editable }) {
  const host = useHostElement();
  const { isGtSmScreen } = useBreakpoints();
  const listFocused = shallowRef(false);
  const hintCleared = shallowRef(false);
  const showHint = computed(() => field.open.value && !listFocused.value && !hintCleared.value);

  function handleSelectFocusIn(event) {
    listFocused.value = event.target === event.currentTarget;
  }

  function handleSelectFocusOut() {
    listFocused.value = false;
  }

  function resetHint(isOpen) {
    if (!isOpen) {
      listFocused.value = false;
      hintCleared.value = false;
    }
  }

  function focusList(location) {
    select.value?.focusListItem?.(location);
  }

  function handleKeyDown(event) {
    field.applyActivatorKeydown(event, focusList);
  }

  function handleInput(event) {
    field.applyInput(event);
    // A stale selection would swallow re-picking the same suggestion after typing over it.
    query.clearSelection();
    hintCleared.value = false;
  }

  function handleUpdateOpen(event) {
    const isOpen = field.applyOpen(event);
    if (isOpen) {
      field.flushSearch();
    }
    // The mobile sheet covers the field, so closing is its blur-equivalent commit.
    if (!isOpen && !isGtSmScreen.value) {
      query.commitFreeTextIfChanged();
    }
  }

  function handleExitStart() {
    hintCleared.value = true;
    input.value?.inputElement?.focus?.();
  }

  // Legacy focus selects the text only on opening, not on return from the list.
  function handleFocusIn() {
    if (!editable.value) {
      return;
    }
    if (!field.open.value) {
      input.value?.inputElement?.select?.();
    }
    field.open.value = true;
  }

  function handleFieldClick() {
    if (editable.value) {
      field.open.value = true;
    }
  }

  // Enter chooses the marked row. With the hint cleared it only closes; it
  // never submits free text (legacy keyboard contract).
  async function handleKeyDownEnter() {
    if (!field.open.value) {
      query.commitFreeTextIfChanged();
      return;
    }
    if (hintCleared.value) {
      field.open.value = false;
      return;
    }
    field.flushSearch();
    await nextTick();
    const candidate = select.value?.firstResult?.();
    if (!candidate) {
      query.commitFreeTextIfChanged();
      return;
    }
    query.commitItem(candidate);
    field.open.value = false;
  }

  function handleUpdateSelectedItem(event) {
    const item = firstDetail(event);
    if (item) {
      query.commitItem(item);
    }
  }

  function reveal() {
    if (!editable.value) {
      return;
    }
    host?.scrollIntoView?.({ block: "nearest" });
    field.flushSearch();
    input.value?.inputElement?.focus?.();
    handleFocusIn();
  }

  watch(field.open, resetHint);

  return {
    showHint,
    reveal,
    handleSelectFocusIn,
    handleSelectFocusOut,
    handleKeyDown,
    handleInput,
    handleUpdateOpen,
    handleExitStart,
    handleFocusIn,
    handleFieldClick,
    handleKeyDownEnter,
    handleUpdateSelectedItem,
  };
}
